import wrapper from 'wrapper';

/**
 * Copyright Google. Apache License. Modified by sc0ttj:
 *
 * - removed calls to document.querySelector
 *   - config.container (elem) replaces config.containerSelector (string)
 *   - config.scenes (NodeList) replaces config.panelSelector (string)
 * - removed "chart" stuff, and fullscreenChart stuff:
 *  - use only scenes for defining/dividing scroll content
 * - renamed callbacks available in config:
 *   - enterHandler() => enter()
 *   - exitHandler() => exit()
 *   - progressHandler() => progress()
 * - improved the data/object passed to the callbacks, so each receives:
 *   - { scene, element, progress, direction }
 * - simplified CSS used in example (see scrollytell.html)
 * - fixes for BBC CPS pages
 *
 * Usage:
 *
 *  import ScrollStory from "./scrollstory.js";
 *
 *  var story = new ScrollStory({
 *      container: document.querySelector(".container"),
 *      scenes: document.querySelectorAll(".container p"),
 *      // define what happens on "enter", "progress" and "exit" of scenes
 *      enter: d => console.log(d),
 *      progress: d => console.log(d),
 *      exit: d => console.log(d),
 *  });
 *
 */

/**
 * The context for all animation corresponding to a container element.
 */
function ScrollStory(config) {
  this.config = config
  this.activeSceneIndex = -1
  this.progressValue = -1
  this.totalProgressValue = -1
  this.frameCount = 0
  this.prevScrollPos = 0
  this.container = config.container
  if (!this.container) {
    throw Error("container not found.")
  }
  this.wrapperOffset = parseInt(wrapper.getScrollPosition(), 10);
  // this.container.style.overflowY = "scroll" // sometimes breaks `position: sticky`, also doesn't seem to be needed
  this.container.style.position = "relative"
  this.scenes = config.scenes
  if (!this.scenes) {
    throw Error("'scenes' not found.")
  }

  // set the position of the guideline which elements pass through to trigger scrolly events
  this.guideline = config.guideline ? window.innerHeight / 100 * config.guideline : window.innerHeight / 2;

  // Draw the guideline onto the screen for debugging (WIP)
  if (config.debug === true) {
    this.guidelineColor = Math.floor(Math.random()*16777215).toString(16);
    this.guidelineElement = document.createElement('div')
    this.guidelineElement.innerText = this.container.getAttribute('class');
    this.guidelineElement.classList.add('scrolly-guideline');
    const style = this.guidelineElement.style;
    style.position = `absolute`;
    style.left = `0px`;
    style.height = `2px`;
    style.width = `99vw`;
    style.borderTop = `2px solid #${this.guidelineColor}`;
    this.container.append(this.guidelineElement);
  }


  this.getScrollTop = () => {
    const el = this.scenes[this.activeSceneIndex === -1 ? 0 : this.activeSceneIndex].getBoundingClientRect()
    return this.wrapperOffset + wrapper.getScrollPosition() - el.top
  }

  this.container.getScrollTop = () => {
    const el = this.container.getBoundingClientRect()
    return this.wrapperOffset + wrapper.getScrollPosition() - el.top
  }

  /**
   * @returns {boolean} If true, the page is scrolling down
   */
  this.isScrollingDown = function() {
    const currentOffset = this.container.getScrollTop()
    const isScrollingDown = currentOffset > this.prevScrollPos
    this.prevScrollPos = currentOffset
    return isScrollingDown
  }

  this.render = function() {
    // Take care not to do work if no scrolling has occurred. This is an
    // important optimization because it can save power on mobile devices.
    const scrollTop = this.container.getScrollTop()
    if (scrollTop === this.getScrollTop()) {
      window.requestAnimationFrame(this.fn)
      return
    }
    this.scrollTop = scrollTop
    // Determine the guideline Y coordinate.
    const cbox = this.container.getBoundingClientRect()
    // @TODO update `this.guideline` here?
    // this.guideline = (cbox.top + cbox.bottom) / 2 // original value
    if (config.debug === true) this.guidelineElement.style.top = `${this.guideline - cbox.top}px`;

    // Determine the active scene and progress value.
    const prevActiveSceneIndex = this.activeSceneIndex
    const prevProgressValue = this.progressValue
    this.activeSceneIndex = -1
    this.progressValue = -1
    for (const [index, scene] of this.scenes.entries()) {
      // get scene progress
      const pbox = scene.getBoundingClientRect()
      let outside = pbox.top > this.guideline || pbox.bottom < this.guideline
      let active = !outside
      let ratio = (this.guideline - pbox.top) / pbox.height
      if (active) {
        this.activeSceneIndex = index
        this.progressValue = ratio
        // get total progress (progress of container through guideline)
        // NOTE: dont use this.container, cos we might have a sticky-container
        // *inside* the main container, so just get the parent node of the
        // current set of scenes
        const parent = scene.parentNode.getBoundingClientRect();
        outside = parent.top > this.guideline || parent.bottom < this.guideline
        active = !outside
        ratio = (this.guideline - parent.top) / parent.height
        if (active) {
          this.totalProgressValue = ratio
        }
        break
      }
    }
    const sceneChanged = prevActiveSceneIndex !== this.activeSceneIndex
    const progressChanged = prevProgressValue !== this.progressValue
    const scrollDirection = this.isScrollingDown() ? "down" : "up"

    // Get the scroll speed
    this.newPos = this.totalProgressValue;
    if (this.lastPos !== null) this.scrollSpeed = parseFloat((this.newPos - this.lastPos) * 100).toFixed(2);
    this.lastPos = this.newPos;

    // Trigger scrollytelling events.
    if (sceneChanged) {
      if (this.config.exit && this.scenes[prevActiveSceneIndex]) {
        this.config.exit({
          index: prevActiveSceneIndex,
          element: this.scenes[prevActiveSceneIndex],
          progress: this.progressValue,
          totalProgress: this.totalProgressValue,
          speed: this.scrollSpeed,
          direction: scrollDirection
        })
      }
      if (this.config.enter && this.scenes[this.activeSceneIndex]) {
        this.config.enter({
          index: this.activeSceneIndex,
          element: this.scenes[this.activeSceneIndex],
          progress: this.progressValue,
          totalProgress: this.totalProgressValue,
          speed: this.scrollSpeed,
          direction: scrollDirection
        })
      }
    }
    // Do not update the frame count when scrolling between scenes (i.e.
    // when there is no active scene).
    if (progressChanged || sceneChanged) {
      this.frameCount += 1
      if (this.config.progress && this.scenes[this.activeSceneIndex]) {
        this.config.progress({
          index: this.activeSceneIndex,
          element: this.scenes[this.activeSceneIndex],
          progress: this.progressValue,
          totalProgress: this.totalProgressValue,
          speed: this.scrollSpeed,
          direction: scrollDirection
        })
      }
    }
    window.requestAnimationFrame(this.fn)
  }

  this.fn = this.render.bind(this)
  window.requestAnimationFrame(this.fn)

  /**
   * Returns the zero-based index of the scene that is currently overlapping
   * the guideline. Returns -1 if no such scene exists.
   */
  this.getActiveSceneIndex = function() {
    return this.activeSceneIndex
  }

  /**
   * Returns a percentage in the range [0, 1] that represents the position of
   * the active scene relative to the guideline. Returns 0 when the top of the
   * scene aligns with the guideline, +1 when the bottom of the scene aligns
   * with the guideline, and -1 if no scene is overlapping the guideline.
   */
  this.getProgressValue = function() {
    return this.progressValue
  }

  /**
   * Forces a re-assessment of the active scene index and progress value. Also forces
   * the progress handler to trigger on the subsequent frame.
   */
  this.refresh = function() {
    this.scrollTop = undefined
    this.activeSceneIndex = -2
  }
}

export default ScrollStory
